import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  HiUsers,
  HiUser,
  HiTruck,
  HiStar,
  HiPlus,
  HiBell,
  HiLogout,
  HiEye,
  HiTrendingUp,
  HiClock,
  HiLocationMarker,
  HiPhone,
  HiSparkles,
  HiExclamation,
  HiSearch,
  HiPencil,
  HiDocument,
  HiCheckCircle,
  HiXCircle,
  HiRefresh,
  HiShieldCheck
} from 'react-icons/hi';
import { FaWallet, FaMoneyBillWave } from 'react-icons/fa';
import { API_URL } from '../../config/apiConfig';
import VendorWalletCard from '../../components/Vendor/VendorWalletCard';

import VendorDriverWalletTable from '../../components/Vendor/VendorDriverWalletTable';
import VendorCommissionPanel from '../../components/Vendor/VendorCommissionPanel';
import VendorCommissionCard from '../../components/Vendor/VendorCommissionCard';
import VendorWalletDisplay from '../../components/Vendor/VendorWalletDisplay';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';
import socketService from '../../utils/socketService';
import { getFcmToken } from '../../firebase/getFcmToken';
import { getVendorWalletSummary, getVendorCommissionSettings } from '../../features/vendor/vendorWalletSlice';

const VendorDashboard = () => {
  const dispatch = useDispatch();
  const [vendorData, setVendorData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ fullName: '', businessName: '', phone: '', state: '', city: '', street: '', landmark: '', pinCode: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [expandedRouteId, setExpandedRouteId] = useState(null);

  // Booking state
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingSearch, setBookingSearch] = useState('');
  const [bookingPage, setBookingPage] = useState(1);
  const bookingPageSize = 10;
  const [bookingPagination, setBookingPagination] = useState({ currentPage: 1, totalPages: 1, totalBookings: 0 });

  // Marketplace state
  const [availableBookings, setAvailableBookings] = useState([]);
  const [availablePage, setAvailablePage] = useState(1);
  const [availableTotalPages, setAvailableTotalPages] = useState(1);
  const [isClaiming, setIsClaiming] = useState(false);
  const [availableCount, setAvailableCount] = useState(0);

  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Handle wallet navigation -> dedicated pages
  const handleWalletNavigation = (walletPage) => {
    if (walletPage === 'wallet') {
      navigate('/vendor/wallet/history');
    } else if (walletPage === 'commission') {
      navigate('/vendor/wallet/withdraw');
    }
  };

  // Connect to socket service on mount
  useEffect(() => {
    socketService.connect();
  }, []);

  // Register/update FCM token on dashboard load
  useEffect(() => {
    const registerFcmToken = async () => {
      try {
        const token = await getFcmToken();
        if (token) {
          console.log('Registering/updating vendor FCM token:', token);
          await axios.post(`${API_URL}/user/update-fcm-token`, { fcmToken: token });
          console.log('Vendor FCM token updated successfully');
        }
      } catch (err) {
        console.warn('Failed to register/update vendor FCM token on dashboard mount:', err);
      }
    };

    if (vendorData && vendorData._id) {
      registerFcmToken();
    }
  }, [vendorData]);

  // Set up socket listeners for vendor
  useEffect(() => {
    if (vendorData && vendorData._id) {
      const vendorId = vendorData._id;

      // Join vendor room
      socketService.joinVendorRoom(vendorId);

      // Listen for new ride requests
      const handleNewRideRequest = (data) => {
        console.log('[Socket] New ride request received:', data);
        
        // Refresh dashboard data so the new booking immediately shows up!
        fetchDashboardData();

        // Get details
        const pickupAddr = data.pickup?.address || 'Pickup address';
        const fareAmount = data.fare ? `₹${data.fare}` : '';
        
        toast.info(
          <div>
            <p className="font-bold">New Ride Request Available! 🚗</p>
            <p className="text-sm">Pickup: {pickupAddr}</p>
            {data.fare && <p className="text-sm">Fare: {fareAmount}</p>}
          </div>,
          {
            autoClose: 10000,
            onClick: () => {
              setActiveTab('marketplace');
              fetchAvailableBookings(1);
            }
          }
        );
      };

      // Listen for other booking lifecycle updates to refresh data in real-time
      const handleBookingUpdate = (data) => {
        console.log('[Socket] Booking update received:', data);
        fetchDashboardData();
        dispatch(getVendorWalletSummary(vendorId));
        dispatch(getVendorCommissionSettings(vendorId));
      };

      // Listen for wallet and commission updates
      const handleWalletUpdate = (data) => {
        console.log('[Socket] Wallet update received:', data);
        fetchDashboardData();
        dispatch(getVendorWalletSummary(vendorId));
        dispatch(getVendorCommissionSettings(vendorId));
        toast.success('Wallet balance updated');
      };

      // Register listeners
      const cleanNewRide = socketService.listenForEvent(socketService.events.NEW_RIDE_REQUEST, handleNewRideRequest);
      const cleanBookingUpdate = socketService.listenForEvent(socketService.events.BOOKING_UPDATED, handleBookingUpdate);
      const cleanBookingStatus = socketService.listenForEvent(socketService.events.BOOKING_STATUS_CHANGED, handleBookingUpdate);
      const cleanWallet = socketService.listenForEvent(socketService.events.WALLET_UPDATED, handleWalletUpdate);
      const cleanCommission = socketService.listenForEvent(socketService.events.COMMISSION_PROCESSED, handleBookingUpdate);
      const cleanWithdrawal = socketService.listenForEvent(socketService.events.WITHDRAWAL_UPDATED, handleBookingUpdate);

      // Cleanup on unmount/vendorData change
      return () => {
        if (cleanNewRide) cleanNewRide();
        if (cleanBookingUpdate) cleanBookingUpdate();
        if (cleanBookingStatus) cleanBookingStatus();
        if (cleanWallet) cleanWallet();
        if (cleanCommission) cleanCommission();
        if (cleanWithdrawal) cleanWithdrawal();
      };
    }
  }, [vendorData]);

  useEffect(() => {
    checkAuth();
    fetchDashboardData();

    // Auto-refresh every 30 seconds to show real-time driver status updates
    const refreshInterval = setInterval(() => {
      fetchDashboardData();
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('vendorToken');
    const vendor = localStorage.getItem('vendorData');

    if (!token || !vendor) {
      navigate('/vendor/login');
      return;
    }

    try {
      setVendorData(JSON.parse(vendor));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      navigate('/vendor/login');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;

      // Fetch drivers, stats, bookings, and available count
      const [driversRes, statsRes, bookingsRes, availableRes] = await Promise.all([
        axios.get(`${API_URL}/vendor/${vendor._id}/drivers?limit=5`),
        axios.get(`${API_URL}/vendor/${vendor._id}/drivers/stats`),
        axios.get(`${API_URL}/vendor/${vendor._id}/bookings?page=${1}&limit=${bookingPageSize}&status=${bookingFilter}&search=${bookingSearch}`),
        axios.get(`${API_URL}/vendor/${vendor._id}/available-bookings?limit=1`)
      ]);

      if (driversRes.data.status) {
        setDrivers(driversRes.data.data.drivers || []);
      }

      if (statsRes.data.status) {
        setStats(statsRes.data.data || {});
      }

      if (bookingsRes.data.success) {
        setBookings(bookingsRes.data.data.bookings || []);
        setBookingStats(bookingsRes.data.data.summary || {});
        const pg = bookingsRes.data.data.pagination || {};
        setBookingPagination({
          currentPage: pg.currentPage || 1,
          totalPages: pg.totalPages || 1,
          totalBookings: pg.totalBookings || 0
        });
        setBookingPage(pg.currentPage || 1);
      }

      if (availableRes.data.success) {
        setAvailableCount(availableRes.data.data.pagination.totalBookings || 0);
        // If we are on the marketplace tab, refresh the full list too
        if (activeTab === 'marketplace') {
          fetchAvailableBookings(availablePage);
        }
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorBookings = async (page = bookingPage, status = bookingFilter, search = bookingSearch) => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;
      const res = await axios.get(`${API_URL}/vendor/${vendor._id}/bookings?page=${page}&limit=${bookingPageSize}&status=${status}&search=${search}`);
      if (res.data.success) {
        setBookings(res.data.data.bookings || []);
        setBookingStats(res.data.data.summary || {});
        const pg = res.data.data.pagination || {};
        setBookingPagination({
          currentPage: pg.currentPage || page,
          totalPages: pg.totalPages || 1,
          totalBookings: pg.totalBookings || 0
        });
        setBookingPage(pg.currentPage || page);
      }
    } catch (e) {
      console.error('Error fetching vendor bookings:', e);
    }
  };

  const fetchAvailableBookings = async (page = 1) => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;
      const res = await axios.get(`${API_URL}/vendor/${vendor._id}/available-bookings?page=${page}&limit=10`);
      if (res.data.success) {
        setAvailableBookings(res.data.data.bookings || []);
        setAvailableTotalPages(res.data.data.pagination.totalPages || 1);
        setAvailablePage(page);
        setAvailableCount(res.data.data.pagination.totalBookings || 0);
      }
    } catch (err) {
      console.error("Marketplace error:", err);
    }
  };

  const handleClaimBooking = async (bookingId) => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;
      
      setIsClaiming(true);
      const res = await axios.post(`${API_URL}/vendor/${vendor._id}/bookings/${bookingId}/claim`);
      if (res.data.success) {
        toast.success("Ride accepted successfully!");
        // Switch to bookings tab and show the newly claimed ride
        setActiveTab('bookings');
        setBookingFilter('pending');
        fetchVendorBookings(1, 'pending');
        // Also refresh stats
        fetchDashboardData();
      }
      setIsClaiming(false);
    } catch (err) {
      console.error("Claim error:", err);
      toast.error(err.response?.data?.message || "Failed to accept ride");
      setIsClaiming(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vendorToken');
    localStorage.removeItem('vendorData');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/vendor/login');
  };

  // Submit updated profile to backend
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vendorToken');
      const payload = {
        fullName: profileForm.fullName,
        businessName: profileForm.businessName,
        phone: profileForm.phone,
        state: profileForm.state,
        city: profileForm.city,
        completeAddress: {
          street: profileForm.street,
          landmark: profileForm.landmark,
          pinCode: profileForm.pinCode
        }
      };

      const res = await axios.put(`${API_URL}/vendor/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.status) {
        const updated = res.data.data;
        setVendorData(updated);
        localStorage.setItem('vendorData', JSON.stringify(updated));
        toast.success(res.data.message || 'Profile updated successfully');
        setShowEditProfile(false);
      } else {
        toast.error(res.data?.message || 'Failed to update profile');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update profile';
      toast.error(msg);
    }
  };

  // Filter drivers when search term or filters change
  useEffect(() => {
    filterDrivers();
  }, [drivers, searchTerm, statusFilter, currentPage]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Fetch bookings when tab/filter/page/search changes
  useEffect(() => {
    if (activeTab === 'bookings') {
      fetchVendorBookings(bookingPage, bookingFilter, bookingSearch);
    }
  }, [activeTab, bookingPage, bookingFilter, bookingSearch]);

  const filterDrivers = () => {
    let filtered = drivers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((driver) => {
        const nameMatch = driver.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const phoneMatch = driver.phone?.includes(searchTerm);
        const vehicleMatch = driver.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          driver.vehicleModel?.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch || vehicleMatch;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((driver) => {
        switch (statusFilter) {
          case 'available':
            return driver.isAvailable;
          case 'unavailable':
            return !driver.isAvailable;
          case 'verified':
            return driver.documentsVerified;
          case 'unverified':
            return !driver.documentsVerified;
          default:
            return true;
        }
      });
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedDrivers = filtered.slice(startIndex, endIndex);

    setFilteredDrivers(paginatedDrivers);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleDriverStatus = async (driverId, currentStatus) => {
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(
        `${API_URL}/vendor/drivers/${driverId}/status`,
        { isAvailable: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver status updated successfully');
      fetchDashboardData(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  const editDriver = (driver) => {
    setSelectedDriver(driver);
    setShowEditModal(true);
  };

  const viewDriverDocuments = (driver) => {
    setSelectedDriver(driver);
    setShowDocumentModal(true);
  };

  // Booking assignment functions
  const openAssignModal = (booking) => {
    setSelectedBooking(booking);
    setShowAssignModal(true);
  };

  const assignDriverToBooking = async (driverId) => {
    if (!selectedBooking || !vendorData) return;

    setAssigningDriver(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.post(
        `${API_URL}/vendor/${vendorData._id}/bookings/${selectedBooking._id}/assign-driver`,
        { driverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Driver assigned to booking successfully!`);
        setShowAssignModal(false);
        setSelectedBooking(null);
        fetchDashboardData(); // Refresh data
      } else {
        toast.error('Failed to assign driver to booking');
      }
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error(error.response?.data?.message || 'Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const getTotalPages = () => {
    const totalFiltered = drivers.filter((driver) => {
      let matches = true;
      if (searchTerm) {
        matches = driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          driver.phone?.includes(searchTerm) ||
          driver.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          driver.vehicleModel?.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (statusFilter !== 'all' && matches) {
        switch (statusFilter) {
          case 'available':
            matches = driver.isAvailable;
            break;
          case 'unavailable':
            matches = !driver.isAvailable;
            break;
          case 'verified':
            matches = driver.documentsVerified;
            break;
          case 'unverified':
            matches = !driver.documentsVerified;
            break;
        }
      }
      return matches;
    }).length;
    return Math.ceil(totalFiltered / itemsPerPage);
  };

  const getAvailabilityColor = (isAvailable) => {
    return isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getAvailabilityText = (isAvailable) => {
    return isAvailable ? 'Available' : 'Offline';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
          <p className="text-indigo-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Premium Glassmorphic Header */}
      <header className="bg-white sticky top-0 z-40 border-b border-slate-100 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 justify-between items-center">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 flex items-center justify-center shadow-md">
                <HiSparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent uppercase tracking-wider">
                  CabZigo
                </h1>
                <p className="text-[10px] text-gray-400 font-extrabold uppercase tracking-widest leading-none mt-0.5">Vendor Portal</p>
              </div>
            </div>

            {/* Quick User actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-4 mr-2 border-r border-gray-100 pr-4">
                <div className="text-right">
                  <p className="text-[10px] text-gray-400 font-black uppercase tracking-wider">Welcome back</p>
                  <p className="font-extrabold text-gray-900 text-sm tracking-tight">{vendorData?.fullName || vendorData?.email}</p>
                  {vendorData?.businessName && <p className="text-xs text-indigo-600 font-bold">{vendorData.businessName}</p>}
                </div>
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-extrabold shadow-sm relative">
                  {(vendorData?.fullName || 'V').charAt(0).toUpperCase()}
                  <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-green-500 border-2 border-white"></span>
                </div>
              </div>

              {/* Profile edit button */}
              <button
                onClick={() => {
                  setProfileForm({
                    fullName: vendorData?.fullName || '',
                    businessName: vendorData?.businessName || '',
                    phone: vendorData?.phone || '',
                    state: vendorData?.state || '',
                    city: vendorData?.city || '',
                    street: vendorData?.completeAddress?.street || '',
                    landmark: vendorData?.completeAddress?.landmark || '',
                    pinCode: vendorData?.completeAddress?.pinCode || ''
                  });
                  setShowEditProfile(true);
                }}
                className="inline-flex items-center px-3.5 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-all font-bold text-xs"
                title="Edit Profile"
              >
                <HiPencil className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Profile</span>
              </button>

              {/* Change password button */}
              <button 
                onClick={() => {
                  if (vendorData) navigate('/vendor/change-password');
                  else navigate('/vendor/forgot-password/send-otp');
                }} 
                className="inline-flex items-center px-3.5 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-100 transition-all font-bold text-xs"
                title="Security"
              >
                <HiShieldCheck className="h-3.5 w-3.5 mr-1 text-slate-500" />
                <span className="hidden sm:inline">Security</span>
              </button>

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3.5 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all font-bold text-xs"
                title="Logout"
              >
                <HiLogout className="h-3.5 w-3.5 mr-1" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8 pb-8">

        {/* Edit Profile Modal */}
        {showEditProfile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex justify-between items-center flex-shrink-0">
                <div>
                  <h3 className="text-lg font-bold text-white">Edit Profile</h3>
                  <p className="text-indigo-200 text-sm">Update your vendor information</p>
                </div>
                <button
                  onClick={() => setShowEditProfile(false)}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <HiXCircle className="h-5 w-5" />
                </button>
              </div>

              {/* Scrollable Form Body */}
              <div className="overflow-y-auto flex-1 px-6 py-5">
                <form id="profileEditForm" onSubmit={handleProfileSubmit} className="space-y-5">
                  {/* Account Info Section */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Account Info</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          type="email"
                          value={vendorData?.email || ''}
                          readOnly
                          className="block w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-500 px-4 py-2.5 text-sm cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={profileForm.fullName}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, fullName: e.target.value }))}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                        <input
                          type="text"
                          value={profileForm.businessName}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, businessName: e.target.value }))}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                        />
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Contact & Location Section */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contact & Location</p>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={profileForm.phone}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input
                            type="text"
                            value={profileForm.state}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, state: e.target.value }))}
                            className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            value={profileForm.city}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                            className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                          <input
                            type="text"
                            value={profileForm.pinCode}
                            onChange={(e) => setProfileForm(prev => ({ ...prev, pinCode: e.target.value }))}
                            className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-100" />

                  {/* Address Section */}
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Address Details</p>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Street / Address</label>
                        <textarea
                          value={profileForm.street}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, street: e.target.value }))}
                          rows={2}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                        <input
                          type="text"
                          value={profileForm.landmark}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, landmark: e.target.value }))}
                          className="block w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-shadow"
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              {/* Fixed Footer */}
              <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowEditProfile(false)}
                  className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="profileEditForm"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modern Welcome Greeting Banner */}
        <div className="bg-gradient-to-r from-indigo-900 via-indigo-800 to-purple-900 rounded-3xl p-6 md:p-8 text-white shadow-xl mb-8 relative overflow-hidden">
          <div className="absolute right-0 top-0 bottom-0 opacity-10 flex items-center pr-12 pointer-events-none">
            <HiSparkles className="w-64 h-64 text-white" />
          </div>
          <div className="relative z-10 max-w-2xl">
            <span className="bg-indigo-500/30 text-indigo-200 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full border border-indigo-500/20">
              Fleet Overview
            </span>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight mt-3 text-white">
              Hello, {vendorData?.fullName || vendorData?.businessName || 'Vendor Partner'}!
            </h2>
            <p className="text-indigo-200/90 mt-2 text-sm md:text-base font-semibold leading-relaxed">
              Manage your fleet, claim new rides from the marketplace, and track your wallet earnings from one centralized dashboard.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={() => { setActiveTab('marketplace'); fetchAvailableBookings(1); }}
                className="bg-white text-indigo-900 hover:bg-indigo-50 px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all shadow-md flex items-center gap-1.5"
              >
                <HiSparkles className="w-4 h-4 text-indigo-600 animate-pulse" />
                Browse Marketplace ({availableCount})
              </button>
              {vendorData?.verificationStatus === 'approved' && (
                <button
                  onClick={() => navigate('/vendor/drivers/add')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all border border-indigo-500/30 shadow-md flex items-center gap-1.5"
                >
                  <HiPlus className="w-4 h-4" />
                  Add a Driver
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Redesigned Metrics Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          {/* Card 1: Total Drivers */}
          <div 
            onClick={() => { setActiveTab('drivers'); setStatusFilter('all'); }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
          >
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Total Drivers</span>
              <p className="text-3xl font-black text-gray-900 tracking-tight">{drivers.length}</p>
              <span className="text-xs font-bold text-indigo-600 group-hover:underline flex items-center gap-1">
                View Fleet →
              </span>
            </div>
            <div className="h-14 w-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm">
              <HiUsers className="h-7 w-7" />
            </div>
          </div>

          {/* Card 2: Available Drivers */}
          <div 
            onClick={() => { setActiveTab('drivers'); setStatusFilter('available'); }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
          >
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Active Drivers</span>
              <p className="text-3xl font-black text-green-600 tracking-tight">
                {drivers.filter(driver => driver.isAvailable).length}
              </p>
              <span className="text-xs font-bold text-green-600 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-ping inline-block"></span>
                On Duty
              </span>
            </div>
            <div className="h-14 w-14 rounded-2xl bg-green-50 text-green-600 flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm">
              <HiUser className="h-7 w-7" />
            </div>
          </div>

          {/* Card 3: Verified Drivers */}
          <div 
            onClick={() => { setActiveTab('drivers'); setStatusFilter('verified'); }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
          >
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Verified Drivers</span>
              <p className="text-3xl font-black text-cyan-600 tracking-tight">
                {drivers.filter(driver => driver.documentsVerified).length}
              </p>
              <span className="text-xs font-bold text-cyan-500 flex items-center gap-1">
                Compliance OK
              </span>
            </div>
            <div className="h-14 w-14 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm">
              <HiCheckCircle className="h-7 w-7" />
            </div>
          </div>

          {/* Card 4: Total Rides */}
          <div 
            onClick={() => { setActiveTab('bookings'); setBookingFilter('all'); }}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 flex items-center justify-between cursor-pointer hover:-translate-y-1 hover:shadow-md transition-all duration-300 group"
          >
            <div className="space-y-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">Total Bookings</span>
              <p className="text-3xl font-black text-purple-600 tracking-tight">{stats.totalRides || 0}</p>
              <span className="text-xs font-bold text-purple-600 group-hover:underline flex items-center gap-1">
                View History →
              </span>
            </div>
            <div className="h-14 w-14 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center transition-transform group-hover:scale-110 shadow-sm">
              <HiTruck className="h-7 w-7" />
            </div>
          </div>
        </div>

        {/* Redesigned Booking Status Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Status 1 */}
          <div 
            onClick={() => { setActiveTab('bookings'); setBookingFilter('pending'); }}
            className="bg-white rounded-2xl p-5 border-l-4 border-amber-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer flex justify-between items-center"
          >
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Pending Assignment</p>
              <p className="text-2xl font-black text-gray-900 mt-2">{bookingStats.pending || 0}</p>
              <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wider text-amber-600 mt-2 bg-amber-50 px-2 py-0.5 rounded-full">
                Needs attention
              </span>
            </div>
            <div className="p-3 bg-amber-50 text-amber-600 rounded-xl">
              <HiClock className="h-6 w-6" />
            </div>
          </div>

          {/* Status 2 */}
          <div 
            onClick={() => { setActiveTab('bookings'); setBookingFilter('assigned'); }}
            className="bg-white rounded-2xl p-5 border-l-4 border-indigo-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer flex justify-between items-center"
          >
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Driver Assigned</p>
              <p className="text-2xl font-black text-gray-900 mt-2">{bookingStats.assigned || 0}</p>
              <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wider text-indigo-600 mt-2 bg-indigo-50 px-2 py-0.5 rounded-full">
                Assigned
              </span>
            </div>
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <HiUsers className="h-6 w-6" />
            </div>
          </div>

          {/* Status 3 */}
          <div 
            onClick={() => { setActiveTab('bookings'); setBookingFilter('inProgress'); }}
            className="bg-white rounded-2xl p-5 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer flex justify-between items-center"
          >
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Active Trips</p>
              <p className="text-2xl font-black text-gray-900 mt-2">{bookingStats.inProgress || 0}</p>
              <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wider text-blue-600 mt-2 bg-blue-50 px-2 py-0.5 rounded-full">
                In progress
              </span>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <HiLocationMarker className="h-6 w-6" />
            </div>
          </div>

          {/* Status 4 */}
          <div 
            onClick={() => { setActiveTab('bookings'); setBookingFilter('completed'); }}
            className="bg-white rounded-2xl p-5 border-l-4 border-emerald-500 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer flex justify-between items-center"
          >
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider leading-none">Completed Trips</p>
              <p className="text-2xl font-black text-gray-900 mt-2">{bookingStats.completed || 0}</p>
              <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wider text-emerald-600 mt-2 bg-emerald-50 px-2 py-0.5 rounded-full">
                Completed
              </span>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <HiCheckCircle className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Segmented Tab Navigation Bar */}
        <div className="flex justify-start mb-6 overflow-x-auto scrollbar-none pb-2 border-b border-gray-100">
          <div className="bg-slate-100 p-1 rounded-2xl flex gap-1 shadow-inner border border-gray-200/40">
            {[
              { id: 'overview', label: 'Overview', icon: null },
              { id: 'drivers', label: 'All Drivers', icon: null },
              { id: 'bookings', label: 'Assigned Bookings', icon: null },
              { id: 'marketplace', label: 'Ride Marketplace', icon: <HiSparkles className="h-4 w-4 mr-1 text-amber-500 animate-pulse" />, badge: availableCount },
              { id: 'wallet', label: 'Wallet System', icon: <FaWallet className="h-3.5 w-3.5 mr-1" /> },
              { id: 'commission', label: 'Commission', icon: <FaMoneyBillWave className="h-3.5 w-3.5 mr-1" /> },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    if (tab.id === 'marketplace') {
                      fetchAvailableBookings(1);
                    }
                  }}
                  className={`flex items-center px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    isActive
                      ? 'bg-white text-indigo-600 shadow-md transform scale-[1.01]'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-white/40'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <span className={`ml-2 px-2 py-0.5 text-[10px] font-black rounded-full leading-none ${
                      isActive ? 'bg-indigo-100 text-indigo-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Dashboard Content Card */}
        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 min-h-[500px]">
          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Vendor Wallet Display with real data */}
                <div>
                  <VendorWalletDisplay
                    vendorId={vendorData?._id}
                    onNavigateToWallet={handleWalletNavigation}
                  />
                </div>

                {/* Recent Drivers */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Recent Drivers
                      </h3>
                      <p className="text-sm text-gray-600">
                        Latest driver registrations in your fleet
                      </p>
                    </div>
                    {vendorData?.verificationStatus === 'approved' && (
                      <button
                        onClick={() => navigate('/vendor/drivers/add')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg transform hover:scale-105"
                      >
                        <HiPlus className="h-5 w-5 mr-2" />
                        Add Driver
                      </button>
                    )}
                  </div>

                  {drivers.length > 0 ? (
                    <div className="space-y-4">
                      {drivers.slice(0, 5).map((driver) => (
                        <div key={driver._id} className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-colors duration-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-bold text-lg">
                                {driver.name.charAt(0)}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {driver.name}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center space-x-4">
                                  <span className="flex items-center">
                                    <HiPhone className="h-4 w-4 mr-1" />
                                    {driver.phone}
                                  </span>
                                  <span className="flex items-center">
                                    <HiTruck className="h-4 w-4 mr-1" />
                                    {driver.vehicleNumber}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getAvailabilityColor(driver.isAvailable)}`}>
                                {getAvailabilityText(driver.isAvailable)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="mx-auto h-24 w-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-4">
                        <HiTruck className="h-12 w-12 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No drivers yet
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {vendorData?.verificationStatus === 'approved'
                          ? 'Start building your fleet by adding your first driver'
                          : 'Add drivers once your account is approved'
                        }
                      </p>
                      {vendorData?.verificationStatus === 'approved' && (
                        <button
                          onClick={() => navigate('/vendor/drivers/add')}
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold"
                        >
                          <HiPlus className="h-5 w-5 mr-2" />
                          Add Your First Driver
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'drivers' && (
              <div>
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      All Drivers
                    </h3>
                    <p className="text-sm text-gray-600">
                      Manage your complete driver fleet
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search drivers..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full sm:w-64 pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm bg-slate-50/50 text-gray-950 font-bold"
                      />
                      <HiSearch className="absolute left-3.5 top-3.5 text-gray-400 h-4 w-4" />
                    </div>

                    {/* Status Filter */}
                    <div className="bg-slate-100 p-1 rounded-2xl flex flex-wrap gap-1 shadow-inner border border-gray-200/40">
                      {[
                        { id: 'all', label: 'All Drivers' },
                        { id: 'available', label: 'Available' },
                        { id: 'unavailable', label: 'Unavailable' },
                        { id: 'verified', label: 'Verified' },
                        { id: 'unverified', label: 'Unverified' }
                      ].map((filter) => {
                        const isActive = statusFilter === filter.id;
                        return (
                          <button
                            key={filter.id}
                            type="button"
                            onClick={() => setStatusFilter(filter.id)}
                            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                              isActive
                                ? 'bg-white text-indigo-600 shadow-md transform scale-[1.01]'
                                : 'text-gray-500 hover:text-gray-800 hover:bg-white/40'
                            }`}
                          >
                            {filter.label}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => navigate('/vendor/drivers/add')}
                      className="inline-flex items-center justify-center px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-lg transform hover:scale-[1.02] whitespace-nowrap"
                    >
                      <HiPlus className="h-4 w-4 mr-1.5" />
                      Add Driver
                    </button>
                  </div>
                </div>

                {filteredDrivers.length > 0 ? (
                  <div>
                    <div className="overflow-x-auto rounded-2xl border border-gray-100 mb-6 shadow-sm">
                      <table className="min-w-full divide-y divide-gray-100 bg-white">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Driver
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Vehicle
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Documents
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Rides
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Rating
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {filteredDrivers.map((driver) => (
                            <tr key={driver._id} className="hover:bg-slate-50/50 transition-colors duration-200">
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-extrabold shadow-sm">
                                    {driver.name?.charAt(0) || 'D'}
                                  </div>
                                  <div className="ml-4">
                                    <div className="font-bold text-gray-900 text-sm">
                                      {driver.name || 'Unknown'}
                                    </div>
                                    <div className="text-xs text-gray-400 font-medium">
                                      {driver.email || 'No email'}
                                    </div>
                                    <div className="text-xs text-indigo-600 font-bold mt-0.5 flex items-center">
                                      <HiPhone className="h-3 w-3 mr-1" />
                                      {driver.phone || 'No phone'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <div className="font-bold text-gray-900 text-sm">
                                  {driver.vehicleNumber || 'N/A'}
                                </div>
                                <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">
                                  {driver.vehicleModel || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${
                                  driver.isAvailable 
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                    : 'bg-slate-100 text-slate-600 border-slate-200'
                                }`}>
                                  <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${driver.isAvailable ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                                  {getAvailabilityText(driver.isAvailable)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${driver.documentsVerified
                                    ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                    : 'bg-amber-50 text-amber-700 border-amber-100'
                                  }`}>
                                  {driver.documentsVerified ? 'Verified' : 'Pending'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900 text-sm">
                                {driver.totalRides || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <div className="flex items-center bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-lg w-max">
                                  <HiStar className="h-4 w-4 text-amber-500 mr-1" />
                                  <span className="font-bold text-amber-700 text-xs">
                                    {driver.ratings ? driver.ratings.toFixed(1) : '0.0'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap align-middle">
                                <div className="flex items-center space-x-1.5">
                                  <button
                                    onClick={() => toggleDriverStatus(driver._id, driver.isAvailable)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all border ${driver.isAvailable
                                        ? 'bg-red-50 border-red-100 text-red-700 hover:bg-red-100'
                                        : 'bg-emerald-50 border-emerald-100 text-emerald-700 hover:bg-emerald-100'
                                      }`}
                                    title={driver.isAvailable ? 'Mark Unavailable' : 'Mark Available'}
                                  >
                                    {driver.isAvailable ? 'Disable' : 'Enable'}
                                  </button>

                                  <button
                                    onClick={() => editDriver(driver)}
                                    className="p-2 rounded-xl text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-all shadow-sm"
                                    title="View Driver Details"
                                  >
                                    <HiEye className="h-4 w-4" />
                                  </button>

                                  <button
                                    onClick={() => viewDriverDocuments(driver)}
                                    className="p-2 rounded-xl text-purple-600 bg-purple-50 hover:bg-purple-100 transition-all shadow-sm"
                                    title="View Documents"
                                  >
                                    <HiDocument className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center text-sm text-gray-500">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, drivers.length)} of {drivers.length} drivers
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>

                          {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 text-sm border rounded-md ${currentPage === page
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                              {page}
                            </button>
                          ))}

                          <button
                            onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                            disabled={currentPage === getTotalPages()}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
                    <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-8 rounded-full mb-6">
                      <HiUser className="h-16 w-16 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {searchTerm || statusFilter !== 'all'
                        ? 'No Drivers Match Your Search'
                        : 'No Drivers Yet'
                      }
                    </h3>
                    <p className="text-gray-600 mb-6 text-center max-w-md">
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search terms or filters'
                        : 'Start building your fleet by adding your first driver'
                      }
                    </p>
                    {(!searchTerm && statusFilter === 'all') && (
                      <button
                        onClick={() => navigate('/vendor/drivers/add')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold"
                      >
                        <HiPlus className="h-5 w-5 mr-2" />
                        Add Your First Driver
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  <div className="lg:col-span-1">
                    <VendorWalletCard
                      vendorId={vendorData?._id}
                      onNavigateToWallet={handleWalletNavigation}
                      commissionOnly={true}
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-lg p-2">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 px-4 pt-4">
                        Dynamic Commission Management
                      </h3>
                      <VendorCommissionPanel
                        vendorId={vendorData?._id}
                        vendorData={vendorData}
                      />
                    </div>
                  </div>
                </div>

                <VendorDriverWalletTable vendorId={vendorData?._id} />
              </div>
            )}

            {activeTab === 'commission' && (
              <div className="space-y-6">
                <VendorCommissionPanel
                  vendorId={vendorData?._id}
                  vendorData={vendorData}
                />
              </div>
            )}

            {activeTab === 'bookings' && (
              <div className="space-y-6">
                {/* Bookings List */}
                <div>
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Assigned Bookings
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Manage and assign drivers to your bookings
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto items-center">
                      {/* Search Input */}
                      <div className="relative w-full sm:w-64">
                        <input
                          type="text"
                          placeholder="Search bookings..."
                          value={bookingSearch}
                          onChange={(e) => {
                            setBookingSearch(e.target.value);
                            setBookingPage(1);
                          }}
                          className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm bg-slate-50/50 text-gray-950 font-bold"
                        />
                        <HiSearch className="absolute left-3.5 top-3.5 text-gray-400 h-4 w-4" />
                      </div>

                      {/* Status Filter */}
                      <div className="bg-slate-100 p-1 rounded-2xl flex flex-wrap gap-1 shadow-inner border border-gray-200/40">
                        {[
                          { id: 'all', label: 'All Bookings' },
                          { id: 'pending', label: 'Pending Assignment' },
                          { id: 'assigned', label: 'Driver Assigned' },
                          { id: 'inProgress', label: 'Trip Active' },
                          { id: 'completed', label: 'Completed' },
                          { id: 'cancelled', label: 'Cancelled' }
                        ].map((filter) => {
                          const isActive = bookingFilter === filter.id;
                          return (
                            <button
                              key={filter.id}
                              type="button"
                              onClick={() => {
                                setBookingFilter(filter.id);
                                setBookingPage(1);
                              }}
                              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                                isActive
                                  ? 'bg-white text-indigo-600 shadow-md transform scale-[1.01]'
                                  : 'text-gray-500 hover:text-gray-800 hover:bg-white/40'
                              }`}
                            >
                              {filter.label}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => fetchVendorBookings(bookingPage, bookingFilter, bookingSearch)}
                        className="inline-flex items-center justify-center px-4 py-2.5 bg-indigo-50 text-indigo-700 rounded-xl hover:bg-indigo-100 transition-all font-bold text-xs uppercase tracking-wider whitespace-nowrap h-[38px]"
                      >
                        <HiRefresh className="h-4 w-4 mr-1.5" />
                        Refresh
                      </button>
                    </div>
                  </div>

                  {(bookingFilter !== 'all' || bookingSearch) && (
                    <div className="mb-4 flex flex-wrap gap-2 items-center">
                      {bookingFilter !== 'all' && (
                        <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs flex items-center gap-1 font-bold border border-indigo-100">
                          Filter: {
                            bookingFilter === 'pending' ? 'Pending Assignment' :
                            bookingFilter === 'assigned' ? 'Driver Assigned' :
                            bookingFilter === 'inProgress' ? 'Trip Active' :
                            bookingFilter === 'completed' ? 'Completed' :
                            bookingFilter === 'cancelled' ? 'Cancelled' : 'All'
                          }
                          <button
                            onClick={() => {
                              setBookingFilter('all');
                              setBookingPage(1);
                            }}
                            className="ml-1 text-[12px] font-bold hover:text-indigo-900 focus:outline-none"
                          >
                            ×
                          </button>
                        </span>
                      )}
                      {bookingSearch && (
                        <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs flex items-center gap-1 font-bold border border-indigo-100">
                          Search: "{bookingSearch}"
                          <button
                            onClick={() => {
                              setBookingSearch('');
                              setBookingPage(1);
                            }}
                            className="ml-1 text-[12px] font-bold hover:text-indigo-900 focus:outline-none"
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </div>
                  )}
                  {bookings.filter(b => (
                    bookingFilter === 'all' ? true :
                      bookingFilter === 'pending' ? ['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(b.status) :
                        bookingFilter === 'assigned' ? b.status === 'assigned' :
                          bookingFilter === 'inProgress' ? b.status === 'inProgress' :
                            bookingFilter === 'completed' ? b.status === 'completed' :
                              bookingFilter === 'cancelled' ? b.status === 'cancelled' : true
                  )).length > 0 ? (
                    <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm mb-6">
                      <table className="min-w-full divide-y divide-gray-100 bg-white">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Booking
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Passenger
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Route
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Car Type
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Ride Type
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Booking Type
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Date & Time
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Assigned Driver
                            </th>
                            <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                          {bookings
                            .filter(b => (
                              bookingFilter === 'all' ? true :
                                bookingFilter === 'pending' ? ['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(b.status) :
                                  bookingFilter === 'assigned' ? b.status === 'assigned' :
                                    bookingFilter === 'inProgress' ? b.status === 'inProgress' :
                                      bookingFilter === 'completed' ? b.status === 'completed' :
                                        bookingFilter === 'cancelled' ? b.status === 'cancelled' : true
                            ))
                            .map((booking) => (
                              <tr key={booking._id} className="hover:bg-slate-50/50 transition-colors duration-200">
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div>
                                    <div className="font-extrabold text-indigo-600 text-sm">
                                      #{booking.bookingId}
                                    </div>
                                    <div className="text-xs font-bold text-gray-900 mt-0.5">
                                      ₹{booking.totalAmount}
                                    </div>
                                    {booking.advancePaid > 0 ? (
                                      <div className="mt-1 space-y-0.5">
                                        <div className="text-[10px] text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded border border-green-100 inline-block">
                                          Adv: ₹{booking.advancePaid}
                                        </div>
                                        <div className="text-[10px] text-orange-600 font-bold bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100 block">
                                          Collect: ₹{booking.remainingAmount !== undefined ? booking.remainingAmount : (booking.totalAmount - booking.advancePaid)}
                                        </div>
                                      </div>
                                    ) : (
                                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border inline-block mt-1 ${
                                        (booking.paymentMethod || 'COD').toUpperCase() === 'COD'
                                          ? 'bg-rose-50 text-rose-700 border-rose-100'
                                          : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                      }`}>
                                        {(booking.paymentMethod || 'COD').toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div>
                                    <div className="font-bold text-gray-900 text-sm">
                                      {booking.user?.name || 'N/A'}
                                    </div>
                                    <div className="text-xs text-gray-400 font-semibold mt-0.5">
                                      {booking.user?.phone || 'N/A'}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 align-middle" style={{ maxWidth: '220px' }}>
                                  <div className="text-xs relative">
                                    {/* Compact route display */}
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px]">📍</span>
                                      <span className="font-bold text-gray-900 truncate block" style={{ maxWidth: '160px' }} title={booking.pickupLocation?.name || booking.from}>
                                        {(booking.pickupLocation?.name || booking.from || '').split(',')[0]}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-1">
                                      <span className="text-[10px]">🏁</span>
                                      <span className="text-gray-500 font-medium truncate block" style={{ maxWidth: '160px' }} title={booking.dropLocation?.name || booking.to}>
                                        {(booking.dropLocation?.name || booking.to || '').split(',')[0]}
                                      </span>
                                    </div>

                                    {/* Eye icon to toggle address details */}
                                    {(booking.pickupAddress || booking.pickupAddressDetails?.landmark || booking.dropAddress || booking.dropAddressDetails?.landmark) && (
                                      <button
                                        type="button"
                                        onClick={() => setExpandedRouteId(expandedRouteId === booking._id ? null : booking._id)}
                                        className="mt-2 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-[10px] font-bold uppercase tracking-wider transition-colors"
                                        title="View full address details"
                                      >
                                        <HiEye className="h-3 w-3" />
                                        {expandedRouteId === booking._id ? 'Hide' : 'Details'}
                                      </button>
                                    )}

                                    {/* Expanded address details dropdown */}
                                    {expandedRouteId === booking._id && (
                                      <div className="absolute left-0 top-full mt-2.5 z-30 bg-white border border-gray-150 rounded-2xl shadow-xl p-4 w-80">
                                        {/* Pickup Details */}
                                        <div className="mb-3">
                                          <p className="text-[9px] font-black text-green-700 uppercase tracking-widest mb-1 flex items-center gap-1">📍 Pickup Location</p>
                                          <p className="text-xs font-bold text-gray-900 mb-0.5">{booking.pickupLocation?.name || booking.from}</p>
                                          {booking.pickupAddress && <p className="text-[11px] text-gray-500 leading-tight">Address: {booking.pickupAddress}</p>}
                                          {booking.pickupAddressDetails?.landmark && <p className="text-[11px] text-gray-400">Landmark: {booking.pickupAddressDetails.landmark}</p>}
                                          {booking.pickupAddressDetails?.pincode && <p className="text-[11px] text-gray-400">PIN: {booking.pickupAddressDetails.pincode}</p>}
                                        </div>
                                        <hr className="border-gray-100 my-2" />
                                        {/* Drop Details */}
                                        <div>
                                          <p className="text-[9px] font-black text-red-600 uppercase tracking-widest mb-1 flex items-center gap-1">🏁 Drop Location</p>
                                          <p className="text-xs font-bold text-gray-900 mb-0.5">{booking.dropLocation?.name || booking.to}</p>
                                          {booking.dropAddress && <p className="text-[11px] text-gray-500 leading-tight">Address: {booking.dropAddress}</p>}
                                          {booking.dropAddressDetails?.landmark && <p className="text-[11px] text-gray-400">Landmark: {booking.dropAddressDetails.landmark}</p>}
                                          {booking.dropAddressDetails?.pincode && <p className="text-[11px] text-gray-400">PIN: {booking.dropAddressDetails.pincode}</p>}
                                          {booking.dropAddressDetails?.additionalInfo && <p className="text-[11px] text-gray-400">Info: {booking.dropAddressDetails.additionalInfo}</p>}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td>

                                {/* Car Type */}
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div className="text-[11px] font-bold text-purple-700 bg-purple-50 border border-purple-100 inline-block px-2.5 py-1 rounded-lg">
                                    🚗 {booking.cabType?.name || booking.cabType || booking.cabName || 'Standard Cab'}
                                  </div>
                                </td>

                                {/* Ride Type */}
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div className={`text-[11px] font-bold border inline-block px-2.5 py-1 rounded-lg ${booking.serviceType === 'airport'
                                      ? 'bg-orange-50 text-orange-700 border-orange-100'
                                      : booking.tripType === 'roundTrip'
                                        ? 'bg-purple-50 text-purple-700 border-purple-100'
                                        : (booking.tripType === 'multiCity' || (booking.viaCities && booking.viaCities.length > 0))
                                          ? 'bg-green-50 text-green-700 border-green-100'
                                          : 'bg-blue-50 text-blue-700 border-blue-100'
                                    }`}>
                                    {(() => {
                                      if (booking.serviceType === 'airport') {
                                        if (booking.transferDirection === 'to' || booking.airportDirection === 'to') {
                                          return '✈️ To Airport';
                                        } else if (booking.transferDirection === 'from' || booking.airportDirection === 'from') {
                                          return '🛬 From Airport';
                                        } else {
                                          return '✈️ Airport';
                                        }
                                      } else if (booking.serviceType === 'rental') {
                                        return '🏙️ Rental';
                                      } else if (booking.tripType === 'roundTrip') {
                                        return '🔄 Round Trip';
                                      } else if (booking.tripType === 'multiCity' || (booking.viaCities && booking.viaCities.length > 0)) {
                                        return '🗺️ Multi-City';
                                      } else {
                                        return '➡️ One Way';
                                      }
                                    })()}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div className={`text-[11px] font-bold border inline-block px-2.5 py-1 rounded-lg ${
                                    booking.advancePaid > 0
                                      ? 'bg-green-50 text-green-700 border-green-100'
                                      : (booking.paymentMethod || 'COD').toUpperCase() === 'COD'
                                        ? 'bg-rose-50 text-rose-700 border-rose-100'
                                        : (booking.paymentMethod || 'COD').toUpperCase() === 'WALLET'
                                          ? 'bg-blue-50 text-blue-700 border-blue-100'
                                          : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                  }`}>
                                    {booking.advancePaid > 0
                                      ? '💳 Advance Paid'
                                      : (booking.paymentMethod || 'COD').toUpperCase() === 'COD'
                                        ? '💵 COD Ride'
                                        : (booking.paymentMethod || 'COD').toUpperCase() === 'WALLET'
                                          ? '💼 Wallet Ride'
                                          : '🌐 Online Paid'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div className="text-xs">
                                    <div className="font-bold text-gray-900">
                                      {formatDateDDMMYYYY(booking.pickupDate || booking.travelDate || booking.date)}
                                    </div>
                                    <div className="text-gray-400 font-semibold mt-0.5">
                                      {booking.pickupTime}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border ${['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(booking.status)
                                      ? 'bg-amber-50 text-amber-700 border-amber-100'
                                      : booking.status === 'assigned'
                                        ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                                        : booking.status === 'inProgress'
                                          ? 'bg-blue-50 text-blue-700 border-blue-100'
                                          : booking.status === 'completed'
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                            : 'bg-slate-100 text-slate-600 border-slate-200'
                                    }`}>
                                    {['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(booking.status) ? 'Pending Assignment' :
                                      booking.status === 'assigned' ? 'Driver Assigned' :
                                        booking.status === 'inProgress' ? 'In Progress' :
                                          booking.status === 'completed' ? 'Completed' : booking.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  <div className="flex items-center space-x-3 max-w-[240px]">
                                    {(() => {
                                      const assigned = [];
                                      if (booking.vendorDriver) assigned.push({ type: 'Vendor', ...booking.vendorDriver });
                                      if (assigned.length === 0) {
                                        return <span className="text-gray-400 italic text-xs font-medium">Not assigned</span>;
                                      }
                                      return assigned.map((d, idx) => (
                                        <div key={idx} className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl px-3 py-1.5 shadow-sm">
                                          <div className="flex items-center space-x-2">
                                            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-xs font-black">
                                              {(d.name || 'D').charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                              <div className="text-xs font-bold text-gray-900">{d.name || 'Driver'}</div>
                                              <div className="text-[10px] text-indigo-600 font-bold">{d.vehicleNumber || d.phone || ''}</div>
                                            </div>
                                          </div>
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap align-middle">
                                  {['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(booking.status) && (
                                    <button
                                      onClick={() => openAssignModal(booking)}
                                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:from-emerald-700 hover:to-teal-700 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                                    >
                                      <HiUsers className="h-3.5 w-3.5 mr-1" />
                                      Assign Driver
                                    </button>
                                  )}
                                  {!['assigned_to_vendor', 'confirmed', 'approved_by_corporate', 'pending', 'Pending', 'searching_driver', 'driver_not_found'].includes(booking.status) && (
                                    <div className="flex items-center text-xs font-bold">
                                      {booking.status === 'assigned' && <HiCheckCircle className="h-4 w-4 text-green-500 mr-1.5" />}
                                      {booking.status === 'inProgress' && <HiClock className="h-4 w-4 text-blue-500 mr-1.5 animate-spin" style={{ animationDuration: '3s' }} />}
                                      {booking.status === 'completed' && <HiCheckCircle className="h-4 w-4 text-emerald-500 mr-1.5" />}
                                      <span className={`${booking.status === 'assigned' ? 'text-green-600' :
                                          booking.status === 'inProgress' ? 'text-blue-600' :
                                            booking.status === 'completed' ? 'text-emerald-600' : 'text-gray-400'
                                        }`}>
                                        {booking.status === 'assigned' ? 'Assigned' :
                                          booking.status === 'inProgress' ? 'Active' :
                                            booking.status === 'completed' ? 'Completed' : 'N/A'}
                                      </span>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                      <div className="px-4 py-3 bg-white">
                        {bookingPagination.totalPages > 1 && (
                          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                            <div className="text-sm text-gray-600">
                              Showing {((bookingPagination.currentPage - 1) * bookingPageSize) + 1} to {Math.min(bookingPagination.currentPage * bookingPageSize, bookingPagination.totalBookings)} of {bookingPagination.totalBookings} bookings
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setBookingPage(Math.max(1, bookingPagination.currentPage - 1))}
                                disabled={bookingPagination.currentPage <= 1}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              {Array.from({ length: bookingPagination.totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => setBookingPage(page)}
                                  className={`px-3 py-1 text-sm border rounded-md ${bookingPagination.currentPage === page
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              ))}
                              <button
                                onClick={() => setBookingPage(Math.min(bookingPagination.totalPages, bookingPagination.currentPage + 1))}
                                disabled={bookingPagination.currentPage >= bookingPagination.totalPages}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
                      <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-8 rounded-full mb-6">
                        <HiDocument className="h-16 w-16 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {bookingSearch || bookingFilter !== 'all' ? 'No Matching Bookings' : 'No Bookings Assigned'}
                      </h3>
                      <p className="text-gray-600 text-center max-w-md">
                        {bookingSearch || bookingFilter !== 'all'
                          ? 'Try adjusting your search terms or filters'
                          : 'When admin assigns bookings to your vendor, they will appear here for you to assign to your drivers.'
                        }
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'marketplace' && (
              <div className="animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Ride Marketplace</h2>
                    <p className="text-gray-600">Accept new confirmed rides available in your area</p>
                  </div>
                  <button 
                    onClick={() => fetchAvailableBookings(availablePage)}
                    className="flex items-center px-4 py-2 bg-white text-gray-700 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    <HiRefresh className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh Pool
                  </button>
                </div>

                {availableBookings.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {availableBookings.map((booking) => (
                      <div key={booking._id} className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between">
                        {/* Premium Dark Gradient Header */}
                        <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 px-6 py-4 relative overflow-hidden flex-shrink-0">
                          <div className="absolute top-0 right-0 p-3 opacity-10 pointer-events-none">
                            <HiSparkles className="w-16 h-16 text-white" />
                          </div>
                          <div className="flex justify-between items-center relative z-10">
                            <div>
                              <p className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Available Booking</p>
                              <span className="font-extrabold text-white text-lg tracking-tight">#{booking.bookingId}</span>
                            </div>
                            <div className="bg-amber-400 text-slate-950 px-3.5 py-1.5 rounded-full font-black text-sm shadow-[0_0_12px_rgba(251,191,36,0.25)]">
                              ₹{booking.totalAmount}
                            </div>
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="p-6 flex-grow flex flex-col justify-between">
                          {/* Route Timeline */}
                          <div className="relative pl-8 space-y-5 mb-6">
                            {/* Vertical Line */}
                            <div className="absolute left-[10px] top-2.5 bottom-2.5 w-[1px] border-l-2 border-dashed border-slate-200"></div>

                            {/* Pickup */}
                            <div className="relative flex items-start">
                              <span className="absolute -left-[27px] h-[20px] w-[20px] rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-[10px] shadow-sm">📍</span>
                              <div className="text-xs">
                                <p className="text-[9px] text-gray-400 font-black uppercase tracking-wider leading-none mb-1">Pickup Location</p>
                                <p className="font-extrabold text-gray-900 text-sm leading-tight">{booking.pickupLocation?.name || booking.from}</p>
                                <p className="text-gray-500 font-semibold mt-1 flex items-center gap-1">
                                  <span>{formatDateDDMMYYYY(booking.pickupDate || booking.travelDate || booking.date)}</span>
                                  <span className="text-gray-300">•</span>
                                  <span>{booking.pickupTime}</span>
                                </p>
                              </div>
                            </div>

                            {/* Drop */}
                            <div className="relative flex items-start">
                              <span className="absolute -left-[27px] h-[20px] w-[20px] rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center text-[10px] shadow-sm">🏁</span>
                              <div className="text-xs">
                                <p className="text-[9px] text-gray-400 font-black uppercase tracking-wider leading-none mb-1">Drop Location</p>
                                <p className="font-extrabold text-gray-900 text-sm leading-tight">{booking.dropLocation?.name || booking.to}</p>
                              </div>
                            </div>
                          </div>

                          {/* Info Badges and Action */}
                          <div>
                            <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100 mb-5">
                              <span className="text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                🚗 {booking.cabType?.name || booking.cabType || booking.cabName || 'Standard'}
                              </span>
                              <span className={`text-[10px] font-black border px-2.5 py-1 rounded-lg uppercase tracking-wider ${
                                booking.serviceType === 'airport'
                                  ? 'bg-orange-50 text-orange-700 border-orange-100'
                                  : booking.tripType === 'roundTrip'
                                    ? 'bg-purple-50 text-purple-700 border-purple-100'
                                    : booking.serviceType === 'rental'
                                      ? 'bg-amber-50 text-amber-700 border-amber-100'
                                      : 'bg-blue-50 text-blue-700 border-blue-100'
                              }`}>
                                {booking.serviceType === 'airport' ? '✈️ Airport' : booking.serviceType === 'rental' ? '🏙️ Rental' : booking.tripType === 'roundTrip' ? '🔄 Round Trip' : '➡️ One Way'}
                              </span>
                              {booking.package && (
                                <span className="text-[10px] font-black text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                  📦 {booking.package}
                                </span>
                              )}
                              {booking.advancePaid > 0 ? (
                                <>
                                  <span className="text-[10px] font-black text-green-700 bg-green-50 border border-green-100 px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                    💳 Adv Paid: ₹{booking.advancePaid}
                                  </span>
                                  <span className="text-[10px] font-black text-orange-700 bg-orange-50 border border-orange-100 px-2.5 py-1 rounded-lg uppercase tracking-wider">
                                    💵 Collect: ₹{booking.remainingAmount !== undefined ? booking.remainingAmount : (booking.totalAmount - booking.advancePaid)}
                                  </span>
                                </>
                              ) : (
                                <span className={`text-[10px] font-black border px-2.5 py-1 rounded-lg uppercase tracking-wider ${
                                  (booking.paymentMethod || 'COD').toUpperCase() === 'COD'
                                    ? 'bg-rose-50 text-rose-700 border-rose-100'
                                    : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                                }`}>
                                  💰 {(booking.paymentMethod || 'COD').toUpperCase()}
                                </span>
                              )}
                            </div>

                            {/* Selected Add-on Services */}
                            {booking.selectedAddOns && booking.selectedAddOns.length > 0 && (
                              <div className="mb-4 p-3 bg-slate-50 border border-slate-100 rounded-2xl">
                                <p className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1">
                                  <span>📦</span> Add-on Services
                                </p>
                                <div className="space-y-1.5">
                                  {booking.selectedAddOns.map((addon, index) => (
                                    <div key={index} className="flex justify-between items-center text-xs font-bold text-gray-700">
                                      <span className="truncate pr-2">{addon.name}</span>
                                      <span className="text-gray-900 font-extrabold flex-shrink-0">₹{addon.price}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <button
                              disabled={isClaiming}
                              onClick={() => handleClaimBooking(booking._id)}
                              className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-2xl font-black text-xs uppercase tracking-wider transition-all transform hover:scale-[1.01] active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5 shadow-md hover:shadow-lg shadow-orange-500/10"
                            >
                              {isClaiming ? (
                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <>
                                  <HiCheckCircle className="h-4 w-4" />
                                  Accept Ride
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl p-20 text-center border-2 border-dashed border-gray-100 shadow-inner">
                    <div className="bg-orange-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <HiSparkles className="h-12 w-12 text-orange-500 animate-pulse" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Pool is empty</h3>
                    <p className="text-gray-600 max-w-md mx-auto text-lg">
                      There are no new available rides in your city right now. They appear here as soon as customers confirm them!
                    </p>
                    <button 
                      onClick={() => fetchAvailableBookings(availablePage)}
                      className="mt-8 px-8 py-3 bg-orange-100 text-orange-600 rounded-2xl font-bold hover:bg-orange-200 transition-all"
                    >
                      Refresh Pool
                    </button>
                  </div>
                )}

                {availableTotalPages > 1 && (
                  <div className="mt-12 flex justify-center space-x-3">
                    {Array.from({ length: availableTotalPages }, (_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => fetchAvailableBookings(i + 1)}
                        className={`w-12 h-12 rounded-xl font-bold transition-all shadow-md ${
                          availablePage === i + 1 
                            ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white scale-110' 
                            : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Driver Modal */}
      {showEditModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh] overflow-hidden border border-gray-150">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex justify-between items-center text-white">
              <div>
                <h2 className="text-xl font-bold">Driver Fleet Profile</h2>
                <p className="text-indigo-200 text-xs mt-0.5">Details for {selectedDriver.name || 'Driver'}</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <HiXCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6">
              {/* Status Badges Group */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 p-5 rounded-2xl flex flex-wrap gap-4 items-center justify-between">
                <div>
                  <h4 className="text-sm font-black text-slate-950 uppercase tracking-wider">Duty & Status</h4>
                  <p className="text-xs text-slate-500 mt-0.5">Real-time fleet activity state</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${
                    selectedDriver.isAvailable
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                      : 'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${selectedDriver.isAvailable ? 'bg-emerald-500' : 'bg-slate-400'}`}></span>
                    {selectedDriver.isAvailable ? 'Available' : 'Offline'}
                  </span>

                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${
                    selectedDriver.isActive
                      ? 'bg-blue-50 text-blue-700 border-blue-100'
                      : 'bg-slate-100 text-slate-600 border-slate-200'
                  }`}>
                    {selectedDriver.isActive ? 'Active' : 'Inactive'}
                  </span>

                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${
                    selectedDriver.isApproved
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-100'
                      : 'bg-rose-50 text-rose-700 border-rose-100'
                  }`}>
                    {selectedDriver.isApproved ? 'Approved' : 'Pending Admin'}
                  </span>
                </div>
              </div>

              {/* Driver and Vehicle Grid */}
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Driver & Vehicle Info</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Driver Name</span>
                    <div className="flex items-center gap-2">
                      <HiUser className="h-4 w-4 text-indigo-500" />
                      <span className="font-bold text-slate-900">{selectedDriver.name || 'N/A'}</span>
                    </div>
                  </div>
                  {/* Phone */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Phone Number</span>
                    <div className="flex items-center gap-2">
                      <HiPhone className="h-4 w-4 text-indigo-500" />
                      <span className="font-bold text-indigo-600">{selectedDriver.phone || 'N/A'}</span>
                    </div>
                  </div>
                  {/* Vehicle Model */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Vehicle Model</span>
                    <div className="flex items-center gap-2">
                      <HiTruck className="h-4 w-4 text-indigo-500" />
                      <span className="font-bold text-slate-900">{selectedDriver.vehicleModel || 'N/A'}</span>
                    </div>
                  </div>
                  {/* Vehicle Number */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Vehicle Number</span>
                    <div className="flex items-center gap-2">
                      <HiTruck className="h-4 w-4 text-indigo-500" />
                      <span className="font-bold text-slate-900">{selectedDriver.vehicleNumber || 'N/A'}</span>
                    </div>
                  </div>
                  {/* Total Rides */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Total Rides completed</span>
                    <div className="flex items-center gap-2">
                      <HiTrendingUp className="h-4 w-4 text-indigo-500" />
                      <span className="font-bold text-slate-900">{selectedDriver.totalRides || '0'}</span>
                    </div>
                  </div>
                  {/* Rating */}
                  <div className="bg-slate-50 border border-slate-150 p-4 rounded-xl">
                    <span className="text-[10px] text-slate-400 font-black uppercase tracking-wider block mb-1">Fleet Rating</span>
                    <div className="flex items-center gap-2">
                      <HiStar className="h-4 w-4 text-amber-500" />
                      <span className="font-bold text-slate-900">{selectedDriver.ratings ? selectedDriver.ratings.toFixed(1) : '0.0'} / 5.0</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Documents Status */}
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Compliance Documents</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'aadhaarCard', label: 'Aadhaar Card' },
                    { key: 'driversLicense', label: 'Driver\'s License' },
                    { key: 'driverPhoto', label: 'Driver Photo' },
                    { key: 'vehicleRC', label: 'Vehicle RC' },
                    { key: 'insuranceCertificate', label: 'Insurance Certificate' },
                    { key: 'pucCertificate', label: 'PUC Certificate' },
                    { key: 'vehiclePhotoFront', label: 'Vehicle Photo (Front)' },
                    { key: 'vehiclePhotoBack', label: 'Vehicle Photo (Back)' }
                  ].map(({ key, label }) => {
                    const fileUrl = selectedDriver.documents?.[key];
                    return (
                      <div key={key} className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 flex flex-col justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-extrabold text-slate-800 text-xs">{label}</h5>
                            <span className={`inline-flex items-center text-[9px] font-black uppercase tracking-wider mt-1 px-2 py-0.5 rounded border ${
                              fileUrl 
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
                                : 'bg-slate-100 border-slate-200 text-slate-500'
                            }`}>
                              {fileUrl ? 'Uploaded' : 'Missing'}
                            </span>
                          </div>
                          <div className={`p-2 rounded-lg ${fileUrl ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-200 text-slate-400'}`}>
                            <HiDocument className="h-4 w-4" />
                          </div>
                        </div>
                        {fileUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(fileUrl, '_blank')}
                            className="mt-3.5 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-700 hover:bg-indigo-100 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                          >
                            <HiEye className="h-3.5 w-3.5" />
                            View Document
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-6 py-2.5 rounded-xl bg-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && selectedDriver && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh] overflow-hidden border border-gray-150">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5 flex justify-between items-center text-white">
              <div>
                <h2 className="text-xl font-bold">Driver Credentials Audit</h2>
                <p className="text-indigo-200 text-xs mt-0.5">Documents verification for {selectedDriver.name || 'Driver'}</p>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <HiXCircle className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 px-6 py-6 space-y-6">
              {/* Overall Status Banner */}
              <div className={`flex items-center justify-between p-5 rounded-2xl border ${
                selectedDriver.documentsVerified
                  ? 'bg-emerald-50 border-emerald-100 text-emerald-800'
                  : 'bg-amber-50 border-amber-100 text-amber-800'
              }`}>
                <div>
                  <h3 className="font-extrabold text-sm uppercase tracking-wider">Compliance Status</h3>
                  <p className="text-xs mt-0.5 opacity-90">
                    {selectedDriver.documentsVerified ? 'All driver credentials are verified and active.' : 'Documents are pending verification by Administrator.'}
                  </p>
                </div>
                <span className={`inline-flex px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${
                  selectedDriver.documentsVerified
                    ? 'bg-white text-emerald-700 border-emerald-200 shadow-sm'
                    : 'bg-white text-amber-700 border-amber-200 shadow-sm'
                }`}>
                  {selectedDriver.documentsVerified ? 'Verified' : 'Pending'}
                </span>
              </div>

              {/* Documents Grid */}
              <div>
                <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Audit Registry</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'aadhaarCard', label: 'Aadhaar Card' },
                    { key: 'driversLicense', label: 'Driver\'s License' },
                    { key: 'driverPhoto', label: 'Driver Photo' },
                    { key: 'vehicleRC', label: 'Vehicle RC' },
                    { key: 'insuranceCertificate', label: 'Insurance Certificate' },
                    { key: 'pucCertificate', label: 'PUC Certificate' },
                    { key: 'vehiclePhotoFront', label: 'Vehicle Photo (Front)' },
                    { key: 'vehiclePhotoBack', label: 'Vehicle Photo (Back)' }
                  ].map(({ key, label }) => {
                    const fileUrl = selectedDriver.documents?.[key];
                    return (
                      <div key={key} className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 flex flex-col justify-between hover:bg-slate-100/50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-extrabold text-slate-800 text-xs">{label}</h5>
                            <span className={`inline-flex items-center text-[9px] font-black uppercase tracking-wider mt-1 px-2 py-0.5 rounded border ${
                              fileUrl 
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700' 
                                : 'bg-slate-100 border-slate-200 text-slate-500'
                            }`}>
                              {fileUrl ? 'Uploaded' : 'Missing'}
                            </span>
                          </div>
                          <div className={`p-2 rounded-lg ${fileUrl ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-200 text-slate-400'}`}>
                            <HiDocument className="h-4 w-4" />
                          </div>
                        </div>
                        {fileUrl && (
                          <button
                            type="button"
                            onClick={() => window.open(fileUrl, '_blank')}
                            className="mt-3.5 inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-indigo-50 border border-indigo-100 text-indigo-700 hover:bg-indigo-100 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                          >
                            <HiEye className="h-3.5 w-3.5" />
                            View Document
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Compliance Notice */}
              <div className="bg-blue-50 border border-blue-150 p-4 rounded-xl text-xs text-blue-700 leading-relaxed mt-6 flex items-start gap-2">
                <HiExclamation className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p>
                  <strong>Compliance Notice:</strong> Vendor drivers are automatically approved upon upload and can accept rides as long as they are set to available. Document verification by admin is for compliance audits only.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowDocumentModal(false)}
                className="px-6 py-2.5 rounded-xl bg-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Driver Assignment Modal */}
      {showAssignModal && selectedBooking && (
        <div className="fixed inset-0 bg-black/55 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full flex flex-col max-h-[90vh] overflow-hidden border border-gray-150">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-purple-50/30 to-indigo-50/40">
              <div>
                <h2 className="text-xl font-black text-gray-900 uppercase tracking-wider">
                  Assign Driver
                </h2>
                <p className="text-xs text-indigo-600 font-extrabold tracking-wider mt-0.5">
                  Booking Reference: #{selectedBooking.bookingId}
                </p>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none transition-all p-1.5 hover:bg-gray-100 rounded-full"
              >
                <HiXCircle className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto flex-1 p-6 space-y-6">
              {/* Booking Details Section Card */}
              <div className="bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 text-white rounded-2xl p-6 relative overflow-hidden shadow-lg">
                <div className="absolute right-0 top-0 opacity-10 pointer-events-none">
                  <HiDocument className="w-48 h-48 text-white" />
                </div>
                <div className="relative z-10">
                  <span className="bg-indigo-500/20 text-indigo-200 border border-indigo-500/20 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
                    Ride Summary
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Passenger</p>
                      <p className="font-extrabold text-white text-sm mt-1">{selectedBooking.user?.name || 'N/A'}</p>
                      <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">{selectedBooking.user?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Pickup Location</p>
                      <p className="font-extrabold text-white text-sm mt-1 truncate" title={selectedBooking.pickupLocation?.name || selectedBooking.from}>
                        📍 {(selectedBooking.pickupLocation?.name || selectedBooking.from || '').split(',')[0]}
                      </p>
                      <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
                        {formatDateDDMMYYYY(selectedBooking.pickupDate || selectedBooking.travelDate || selectedBooking.date)} • {selectedBooking.pickupTime}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Drop Location</p>
                      <p className="font-extrabold text-white text-sm mt-1 truncate" title={selectedBooking.dropLocation?.name || selectedBooking.to}>
                        🏁 {(selectedBooking.dropLocation?.name || selectedBooking.to || '').split(',')[0]}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Trip Amount</p>
                      <p className="font-black text-amber-400 text-lg mt-1">₹{selectedBooking.totalAmount}</p>
                      {selectedBooking.advancePaid > 0 ? (
                        <div className="text-[10px] text-indigo-200 mt-1 flex flex-col space-y-0.5">
                          <span className="text-green-300 font-bold">💳 Adv Paid: ₹{selectedBooking.advancePaid}</span>
                          <span className="text-orange-300 font-bold">💵 Collect: ₹{selectedBooking.remainingAmount !== undefined ? selectedBooking.remainingAmount : (selectedBooking.totalAmount - selectedBooking.advancePaid)}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] text-indigo-200 mt-1 font-bold block">
                          💰 Payment: {(selectedBooking.paymentMethod || 'COD').toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Car Type</p>
                      <p className="font-extrabold text-white text-sm mt-1 uppercase tracking-wider">
                        🚗 {selectedBooking.cabType?.name || selectedBooking.cabType || selectedBooking.cabName || 'Standard'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[9px] text-indigo-300 font-black uppercase tracking-wider leading-none">Ride Type</p>
                      <p className="font-extrabold text-white text-sm mt-1 uppercase tracking-wider">
                        {selectedBooking.serviceType === 'airport' ? '✈️ Airport' : selectedBooking.serviceType === 'rental' ? '🏙️ Rental' : selectedBooking.tripType === 'roundTrip' ? '🔄 Round Trip' : '➡️ One Way'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Available Drivers List */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider">Select Available Driver</h3>
                    <p className="text-xs text-gray-500 mt-0.5">Click on an available driver card to assign them</p>
                  </div>
                  <button
                    onClick={() => fetchDashboardData()}
                    className="px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-700 hover:bg-indigo-100 transition-all font-bold text-xs uppercase rounded-lg"
                  >
                    Refresh List
                  </button>
                </div>

                {drivers.filter(driver => driver.isAvailable && !driver.currentBooking).length > 0 ? (
                  <div className="grid gap-3.5">
                    {drivers
                      .filter(driver => driver.isAvailable && !driver.currentBooking)
                      .map((driver) => (
                        <div
                          key={driver._id}
                          className={`border border-slate-200 bg-slate-50/40 rounded-2xl p-4 hover:border-indigo-300 hover:bg-indigo-50/40 hover:-translate-y-0.5 hover:shadow-md cursor-pointer transition-all duration-200 flex items-center justify-between ${
                            assigningDriver ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => !assigningDriver && assignDriverToBooking(driver._id)}
                        >
                          <div className="flex items-center gap-4">
                            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-extrabold text-lg shadow-sm">
                              {driver.name?.charAt(0) || 'D'}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900 text-base">{driver.name || 'Unknown'}</div>
                              <div className="text-xs text-indigo-600 font-bold flex items-center mt-0.5">
                                <HiPhone className="h-3.5 w-3.5 mr-1" />
                                {driver.phone || 'No phone'}
                              </div>
                            </div>
                          </div>

                          <div className="text-center hidden sm:block">
                            <div className="font-bold text-slate-800 flex items-center justify-center uppercase tracking-wider text-sm">
                              <HiTruck className="h-4 w-4 mr-1 text-slate-500" />
                              {driver.vehicleNumber || 'N/A'}
                            </div>
                            <div className="text-xs text-slate-400 font-semibold uppercase mt-0.5">{driver.vehicleModel || 'N/A'}</div>
                          </div>

                          <div className="flex flex-col items-end space-y-1.5">
                            <div className="flex items-center gap-2">
                              {driver.ratings >= 4.5 && (
                                <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider">Top Rated</span>
                              )}
                              <div className="flex items-center bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-lg">
                                <HiStar className="h-3.5 w-3.5 text-amber-500 mr-1" />
                                <span className="text-xs font-bold text-amber-700">
                                  {driver.ratings ? driver.ratings.toFixed(1) : '0.0'}
                                </span>
                              </div>
                            </div>
                            <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                              {driver.totalRides || 0} rides
                            </div>
                          </div>

                          <div className="flex items-center">
                            {assigningDriver ? (
                              <div className="flex items-center px-4 py-2 border border-indigo-100 bg-indigo-50 rounded-xl">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-200 border-t-indigo-600 mr-2"></div>
                                <span className="text-xs font-black text-indigo-600 uppercase tracking-wider">Assigning</span>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-sm transition-all">
                                Select Driver
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-dashed border-gray-200 shadow-inner">
                    <div className="mx-auto h-16 w-16 rounded-full bg-orange-50 flex items-center justify-center mb-4 shadow-sm">
                      <HiExclamation className="h-8 w-8 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-extrabold text-gray-900 mb-2">No Available Drivers</h3>
                    <p className="text-gray-600 max-w-md mx-auto text-sm leading-relaxed px-4">
                      All your drivers are currently on active trips or offline. Please add a driver or check back once they complete their active trips.
                    </p>
                    <div className="mt-6 flex justify-center gap-3">
                      <button
                        onClick={() => {
                          setShowAssignModal(false);
                          setActiveTab('drivers');
                        }}
                        className="px-5 py-2.5 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition-all font-bold text-xs uppercase tracking-wider"
                      >
                        Manage Fleet
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
